// Per-carrier fleet hook (D3j). Composed by LogisticsCarriersList when a
// row's "Rigs" toggle expands -- fetches rigs filtered by carrier_id +
// owns the add-rig form draft + the in-place edit draft (Plants-style:
// editingId + edit draft on the hook).
//
// Sits beside useLogisticsCarriersPanel rather than expanding it: the
// fleet affordance is orthogonal to the carriers CRUD concerns the main
// hook owns. Same single-hook-per-affordance shape as
// useLogisticsCarrierRouteHistory (the sibling Routes toggle).
//
// Capacity is a value + unit pair so non-shed industries can use the
// same surface without column proliferation; the form keeps the inputs
// gated together (clearing one auto-clears the other on submit to satisfy
// the DB CHECK / service-layer "pair" rule).
import { useCallback, useEffect, useState } from "react";
import {
  createLogisticsCarrierRig,
  listLogisticsCarrierRigs,
  updateLogisticsCarrierRig,
  type LogiCarrierRig,
  type LogiRigCapacityUnit,
} from "@/lib/logistics";

export const RIG_CAPACITY_UNITS: readonly LogiRigCapacityUnit[] = [
  "linear_feet",
  "pounds",
  "kilograms",
  "cubic_feet",
  "cubic_meters",
  "pallets",
  "count",
  "gallons",
  "liters",
];

export const RIG_CAPACITY_UNIT_LABELS: Record<LogiRigCapacityUnit, string> = {
  linear_feet: "linear feet",
  pounds: "lb",
  kilograms: "kg",
  cubic_feet: "cu ft",
  cubic_meters: "cu m",
  pallets: "pallets",
  count: "units",
  gallons: "gal",
  liters: "L",
};

export type RigDraft = {
  designation: string;
  standard_driver_name: string;
  // value as a string so the input round-trips cleanly + empty = unset.
  capacity_value_text: string;
  // "" = unset; non-empty = one of LogiRigCapacityUnit.
  capacity_unit: "" | LogiRigCapacityUnit;
  notes: string;
};

function emptyDraft(): RigDraft {
  return {
    designation: "",
    standard_driver_name: "",
    capacity_value_text: "",
    capacity_unit: "",
    notes: "",
  };
}

function draftFromRig(r: LogiCarrierRig): RigDraft {
  return {
    designation: r.designation,
    standard_driver_name: r.standard_driver_name ?? "",
    capacity_value_text: r.capacity_value === null ? "" : String(r.capacity_value),
    capacity_unit: r.capacity_unit ?? "",
    notes: r.notes,
  };
}

function parseCapacity(text: string): number | null | "invalid" {
  const t = text.trim();
  if (t === "") return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0) return "invalid";
  return n;
}

// Wraps the value + unit fields together. The DB CHECK + service rule
// requires both set or both null; the UI surfaces a single error when
// they disagree.
function shapeCapacityForWire(
  draft: RigDraft
): { ok: true; value: number | null; unit: LogiRigCapacityUnit | null } | { ok: false; error: string } {
  const parsed = parseCapacity(draft.capacity_value_text);
  if (parsed === "invalid") {
    return { ok: false, error: "Capacity value must be a positive number." };
  }
  const valueSet = parsed !== null;
  const unitSet = draft.capacity_unit !== "";
  if (valueSet !== unitSet) {
    return {
      ok: false,
      error: "Capacity value and unit must be set together (or both left blank).",
    };
  }
  return {
    ok: true,
    value: valueSet ? (parsed as number) : null,
    unit: unitSet ? (draft.capacity_unit as LogiRigCapacityUnit) : null,
  };
}

export function useLogisticsCarrierFleet(carrierId: string | null) {
  const [rigs, setRigs] = useState<LogiCarrierRig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<RigDraft>(emptyDraft);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [edit, setEdit] = useState<RigDraft>(emptyDraft);

  const reload = useCallback(async () => {
    if (!carrierId) {
      setRigs([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const res = await listLogisticsCarrierRigs({ carrier_id: carrierId });
    setLoading(false);
    if (!res.ok) {
      setRigs([]);
      setError(res.error);
      return;
    }
    const list = Array.isArray(res.data.rigs) ? res.data.rigs : [];
    setRigs([...list].sort((a, b) => a.designation.localeCompare(b.designation)));
  }, [carrierId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Reset the per-card state whenever the carrierId changes so the
  // previous carrier's draft doesn't bleed into the next card.
  useEffect(() => {
    setAdding(false);
    setDraft(emptyDraft());
    setEditingId(null);
    setEdit(emptyDraft());
    setError(null);
  }, [carrierId]);

  const startAdd = useCallback(() => {
    setAdding(true);
    setEditingId(null);
    setDraft(emptyDraft());
    setError(null);
  }, []);

  const cancelAdd = useCallback(() => {
    setAdding(false);
    setError(null);
  }, []);

  const submitAdd = useCallback(async () => {
    if (!carrierId) return;
    const designation = draft.designation.trim();
    if (!designation) {
      setError("Designation is required.");
      return;
    }
    const cap = shapeCapacityForWire(draft);
    if (!cap.ok) {
      setError(cap.error);
      return;
    }
    setActionBusy(true);
    setError(null);
    const res = await createLogisticsCarrierRig({
      carrier_id: carrierId,
      designation,
      standard_driver_name: draft.standard_driver_name.trim() || undefined,
      capacity_value: cap.value ?? undefined,
      capacity_unit: cap.unit ?? undefined,
      notes: draft.notes.trim() || undefined,
    });
    setActionBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setAdding(false);
    setDraft(emptyDraft());
    await reload();
  }, [carrierId, draft, reload]);

  const startEdit = useCallback((rig: LogiCarrierRig) => {
    setEditingId(rig.id);
    setEdit(draftFromRig(rig));
    setAdding(false);
    setError(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setError(null);
  }, []);

  const submitEdit = useCallback(async () => {
    if (!editingId) return;
    const designation = edit.designation.trim();
    if (!designation) {
      setError("Designation cannot be blank.");
      return;
    }
    const cap = shapeCapacityForWire(edit);
    if (!cap.ok) {
      setError(cap.error);
      return;
    }
    setActionBusy(true);
    setError(null);
    const res = await updateLogisticsCarrierRig({
      rig_id: editingId,
      designation,
      // Three-way: null clears, undefined leaves alone. Empty driver text
      // = explicit clear (rotating-driver carriers want to be able to wipe
      // a name that no longer applies). Empty notes = literal "" (replaces).
      standard_driver_name: edit.standard_driver_name.trim()
        ? edit.standard_driver_name.trim()
        : null,
      capacity_value: cap.value,
      capacity_unit: cap.unit,
      notes: edit.notes,
    });
    setActionBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setEditingId(null);
    await reload();
  }, [editingId, edit, reload]);

  const toggleActive = useCallback(
    async (rig: LogiCarrierRig) => {
      setActionBusy(true);
      setError(null);
      const res = await updateLogisticsCarrierRig({
        rig_id: rig.id,
        is_active: !rig.is_active,
      });
      setActionBusy(false);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      await reload();
    },
    [reload]
  );

  return {
    rigs,
    loading,
    error,
    actionBusy,
    reload,
    adding,
    draft,
    setDraft,
    startAdd,
    cancelAdd,
    submitAdd,
    editingId,
    edit,
    setEdit,
    startEdit,
    cancelEdit,
    submitEdit,
    toggleActive,
  };
}
